import React, { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  TextField,
  MenuItem,
  Button,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { ROLES, normalizeDepartment, resolveDepartmentTabs } from '../shared/constants';
import { useTimesheetTableStyles } from '../shared/tableStyles';
import { useTimesheetPageStyles } from '../shared/pageStyles';
import { useInfiniteRows } from '../shared/useInfiniteRows';

const useStyles = makeStyles(theme => ({
  filters: {
    display: 'flex',
    gap: theme.spacing(1.5),
    flexWrap: 'wrap',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  filter: {
    minWidth: 180,
  },
  search: {
    minWidth: 240,
    flex: '1 1 240px',
    maxWidth: 360,
  },
  clearBtn: {
    height: 40,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    fontWeight: 700,
    fontSize: 12,
    color: '#444651',
    // Force visible border over MUI outlined styles.
    border: '1px solid #c5c5d3 !important',
    borderColor: '#c5c5d3 !important',
    '&:hover': {
      border: '1px solid #c5c5d3 !important',
      borderColor: '#c5c5d3 !important',
    },
    '&.Mui-disabled': {
      border: '1px solid #c5c5d3 !important',
      borderColor: '#c5c5d3 !important',
      opacity: 0.85,
    },
  },
}));

const Assignments = ({ users }) => {
  const classes = useStyles();
  const tableClasses = useTimesheetTableStyles();
  const pageClasses = useTimesheetPageStyles();
  const fms = useMemo(
    () => users.filter(u => u.role === ROLES.FIELD_MANAGER),
    [users],
  );
  const [search, setSearch] = useState('');
  const [department, setDepartment] = useState('all');

  const fmName = id => {
    if (!id) return '—';
    const fm = fms.find(u => u.id === id);
    return fm ? fm.name : id;
  };

  const engineers = useMemo(
    () => users.filter(u => u.role === ROLES.ENGINEER && u.assignedFmId),
    [users],
  );

  const departmentOptions = useMemo(() => {
    const map = {};
    engineers.forEach(eng => {
      const dept = normalizeDepartment(eng.department);
      if (dept) map[dept] = true;
    });
    return resolveDepartmentTabs(map);
  }, [engineers]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return engineers
      .filter(eng => {
        if (
          department !== 'all' &&
          normalizeDepartment(eng.department) !== department
        ) {
          return false;
        }
        if (!q) return true;
        const assigned = fms.find(u => u.id === eng.assignedFmId);
        const hay = `${eng.name || ''} ${eng.email || ''} ${normalizeDepartment(
          eng.department,
        ) || ''} ${assigned ? assigned.name : eng.assignedFmId || ''}`.toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) =>
        (a.name || '').localeCompare(b.name || '', undefined, {
          sensitivity: 'base',
        }),
      );
  }, [engineers, search, department, fms]);

  const filterKey = `${search}|${department}`;
  const { visibleRows, hasMore, onTableScroll, visibleCount, totalCount } =
    useInfiniteRows(filtered, { pageSize: 25, resetKey: filterKey });

  const hasActiveFilters = !!search.trim() || department !== 'all';

  const clearFilters = () => {
    setSearch('');
    setDepartment('all');
  };

  return (
    <Box>
      <Typography className={pageClasses.pageTitle} style={{ marginBottom: 8 }}>
        FM Assignments
      </Typography>
      <Typography color="textSecondary" gutterBottom>
        Engineers mapped to Field Managers for this app.
      </Typography>

      <Box className={classes.filters}>
        <TextField
          className={classes.search}
          label="Search"
          placeholder="Name, email, FM…"
          variant="outlined"
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <TextField
          select
          className={classes.filter}
          label="Department"
          variant="outlined"
          size="small"
          value={department}
          onChange={e => setDepartment(e.target.value)}
        >
          <MenuItem value="all">All departments</MenuItem>
          {departmentOptions.map(d => (
            <MenuItem key={d} value={d}>
              {d}
            </MenuItem>
          ))}
        </TextField>
        <Button
          variant="outlined"
          className={classes.clearBtn}
          onClick={clearFilters}
          disabled={!hasActiveFilters}
        >
          Clear filters
        </Button>
      </Box>

      <TableContainer
        component={Paper}
        className={tableClasses.tablePaper}
        onScroll={onTableScroll}
      >
        <Table className={tableClasses.table} stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Engineer</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Department</TableCell>
              <TableCell>Assigned FM</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4}>
                  <Typography color="textSecondary">
                    No matching assignments.
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              visibleRows.map(eng => (
                <TableRow key={eng.id} hover>
                  <TableCell>{eng.name}</TableCell>
                  <TableCell>{eng.email}</TableCell>
                  <TableCell>{normalizeDepartment(eng.department) || '—'}</TableCell>
                  <TableCell>{fmName(eng.assignedFmId)}</TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        {filtered.length > 0 ? (
          <Typography className={tableClasses.scrollHint}>
            {hasMore
              ? `Showing ${visibleCount} of ${totalCount} — scroll for more`
              : `Showing all ${totalCount} assignments`}
          </Typography>
        ) : null}
      </TableContainer>
    </Box>
  );
};

export default Assignments;
